import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import type {
	CampaignMemberEndedReason,
	CampaignMemberPausedReason,
	CampaignProviderLeadState,
	CampaignStepType,
} from '../../constants/campaign-sequence';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CampaignProviderCampaignEntity } from './campaign-provider-campaign.entity';
import { CampaignEntity } from './campaign.entity';

export type CampaignMembershipState =
	| 'active'
	| 'replied'
	| 'completed'
	| 'unsubscribed'
	| 'bounced'
	| 'suppressed'
	| 'fell_out'
	// PD-17613 — v2 runs: active and paused are running; ended carries
	// why in endedReason.
	| 'paused'
	| 'ended';

export type CampaignReplySentiment = 'positive' | 'negative' | 'neutral';

/**
 * One contact's membership in a Workd campaign. This is the spine of the call /
 * follow-up lists: reps filter on `externalStatus` (the provider's per-contact
 * lead status) plus the denormalized communication signals below.
 *
 * The comm-signal columns (emailCount, hasReplied, lastEmailSentDate, ...) are
 * denormalized from CampaignMessage by the sync so list filters stay fast.
 *
 * A contact is in a Workd campaign once, and in exactly one provider campaign
 * (`fkCampaignProviderCampaignId`). Contact/client links are nullable so the row
 * can exist while identity resolution (lead email -> client_contact) is pending;
 * `emailAddress` is the resolution key in that window.
 *
 * Not auditable: high-volume, sync-maintained.
 */
@Index('campaign_membership__fk_campaign', ['fkCampaignId'], {})
@Index('campaign_membership__fk_contact', ['fkClientContactId'], {})
@Index('campaign_membership__external_lead', ['externalLeadId'], {})
@Index('campaign_membership__state', ['fkCampaignId', 'state'], {})
@Index('campaign_membership__fk_person', ['fkContactId'], {})
@Index('campaign_membership__provider_lead_state', ['providerLeadState', 'endedAt'], {})
@Entity('campaign_membership', {}, { auditable: false })
export class CampaignMembershipEntity extends BaseEntity {
	@Column('int', { name: 'fk_campaign__id', unsigned: true })
	public fkCampaignId!: number;

	@Column('int', {
		name: 'fk_campaign_provider_campaign__id',
		unsigned: true,
		nullable: true,
	})
	public fkCampaignProviderCampaignId!: number | null;

	@Column('int', { name: 'fk_client__id', unsigned: true, nullable: true })
	public fkClientId!: number | null;

	@Column('int', {
		name: 'fk_client_contact__id',
		unsigned: true,
		nullable: true,
	})
	public fkClientContactId!: number | null;

	@Column('varchar', { name: 'externalLeadId', length: 255, nullable: true })
	public externalLeadId!: string | null;

	@Column('varchar', { name: 'emailAddress', length: 255, nullable: true })
	public emailAddress!: string | null;

	@Column('varchar', { name: 'state', length: 50, default: () => "'active'" })
	public state!: CampaignMembershipState | string;

	// The provider's per-contact lead status (e.g. Instantly: interested,
	// not_interested, meeting_booked, ...). The primary filter field for lists.
	@Column('varchar', { name: 'externalStatus', length: 100, nullable: true })
	public externalStatus!: string | null;

	@Column('varchar', { name: 'replySentiment', length: 20, nullable: true })
	public replySentiment!: CampaignReplySentiment | string | null;

	@Column('int', { name: 'emailCount', default: () => '0' })
	public emailCount!: number;

	@Column('datetime', { name: 'lastEmailSentDate', nullable: true })
	public lastEmailSentDate!: Date | null;

	@Column({ type: 'boolean', name: 'hasReplied', width: 1, default: () => "'0'" })
	public hasReplied!: boolean;

	@Column('datetime', { name: 'lastInboundDate', nullable: true })
	public lastInboundDate!: Date | null;

	@Column('datetime', { name: 'lastCallDate', nullable: true })
	public lastCallDate!: Date | null;

	@Column('varchar', { name: 'callOutcome', length: 100, nullable: true })
	public callOutcome!: string | null;

	// Any channel — used as a cooldown guard when recycling contacts through offers.
	@Column('datetime', { name: 'lastContactedDate', nullable: true })
	public lastContactedDate!: Date | null;

	@Column('datetime', { name: 'enrolledDate', nullable: true })
	public enrolledDate!: Date | null;

	@Column('datetime', { name: 'lastSyncedAt', nullable: true })
	public lastSyncedAt!: Date | null;

	// ---------------------------------------------------------------------
	// PD-17613 — campaigns v2. The PERSON is the unit: `fk_contact__id` is
	// who is in the campaign; `fk_client__id` / `fk_client_contact__id` stay
	// as "at which customer" (the record the person was added from). The run
	// columns are maintained by the sequence engine (PD-17614).
	// ---------------------------------------------------------------------

	@Column('int', { name: 'fk_contact__id', unsigned: true, nullable: true })
	public fkContactId!: number | null;

	/** The step the person is on (campaign_step.stepNo of their open step row). */
	@Column('int', { name: 'currentStepNo', unsigned: true, nullable: true })
	public currentStepNo!: number | null;

	/** When their open step comes due (a call-back's time when one is open). */
	@Column('datetime', { name: 'nextActionAt', nullable: true })
	public nextActionAt!: Date | null;

	// App-validated (CampaignStepType).
	@Column('varchar', { name: 'nextActionType', length: 20, nullable: true })
	public nextActionType!: CampaignStepType | string | null;

	@Column('datetime', { name: 'pausedUntil', nullable: true })
	public pausedUntil!: Date | null;

	// App-validated (CampaignMemberPausedReason).
	@Column('varchar', { name: 'pausedReason', length: 40, nullable: true })
	public pausedReason!: CampaignMemberPausedReason | string | null;

	// App-validated (CampaignMemberEndedReason).
	@Column('varchar', { name: 'endedReason', length: 40, nullable: true })
	public endedReason!: CampaignMemberEndedReason | string | null;

	@Column('datetime', { name: 'endedAt', nullable: true })
	public endedAt!: Date | null;

	/** The rep who works this person's manual steps. Null = the campaign owner. */
	@Column('int', { name: 'fk_user__id_rep', unsigned: true, nullable: true })
	public fkUserIdRep!: number | null;

	/** Workd's Reply Category for their reply (catalog_value, Reply Category list). */
	@Column('int', { name: 'fk_catalog_value__id_replyCategory', unsigned: true, nullable: true })
	public fkCatalogValueIdReplyCategory!: number | null;

	// App-validated (CampaignProviderLeadState). The rotation and stop rules
	// read it; null on v1 rows.
	@Column('varchar', { name: 'providerLeadState', length: 20, nullable: true })
	public providerLeadState!: CampaignProviderLeadState | string | null;

	@Column('datetime', { name: 'providerLeadStateDate', nullable: true })
	public providerLeadStateDate!: Date | null;

	/**
	 * When everything Instantly held for this person (sent emails, replies,
	 * statuses) was last confirmed saved in Workd. Rotation removes a person
	 * only when this is after their run ended.
	 */
	@Column('datetime', { name: 'historySavedAt', nullable: true })
	public historySavedAt!: Date | null;

	/** The mailbox that sent their first email; later emails should come from it too. */
	@Column('varchar', { name: 'mailboxEmail', length: 255, nullable: true })
	public mailboxEmail!: string | null;

	/**
	 * PD-17622 — the card's AI notes for this person ("why this call", talking
	 * points, a personalized line), written once when they are first opened
	 * and again after a new touch. Null until written.
	 */
	@Column('json', { name: 'aiNotes', nullable: true })
	public aiNotes!: { whyThisCall: string | null; talkingPoints: Array<string>; personalLine: string | null } | null;

	@Column('datetime', { name: 'aiNotesAt', nullable: true })
	public aiNotesAt!: Date | null;

	@ManyToOne(() => CampaignEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_campaign__id', referencedColumnName: 'id' }])
	public fkCampaign!: CampaignEntity;

	@ManyToOne(() => CampaignProviderCampaignEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_campaign_provider_campaign__id', referencedColumnName: 'id' },
	])
	public fkCampaignProviderCampaign!: CampaignProviderCampaignEntity | null;
}
